import { useSelector } from 'react-redux';
import { Record, OrderedMap } from 'immutable';

// Types
import { FormTypeType } from 'Shared/types/ReviewFormTemplate.type';
import { ReviewType } from 'Shared/types/Review.type';
import { ReviewTemplateVersion, ReviewTemplateTypeType } from 'Shared/types/ReviewTemplates.type';

// Hooks
import { useCurrentUser } from './use-current-user';
import { useUser } from './use-user';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';

// Selectors
import * as ReviewsSelectors from 'Shared/selectors/Reviews.selectors';

type Props = {
  slug: string;
};

export const useReview = ({ slug }: Props) => {
  const { slug: currentUserSlug } = useCurrentUser();

  const review: Record<ReviewType> = useSelector(state =>
    ReviewsSelectors.getReviewBySlug(state, { reviewSlug: slug })
  );

  const reviewJsObject: ReviewType = review?.toJS();

  const { user: receiver } = useUser({ slug: reviewJsObject?.receiverSlug });
  const { user: compiler } = useUser({ slug: reviewJsObject?.compilerSlug });
  const { user: creator } = useUser({ slug: reviewJsObject?.creatorSlug });

  const isPerformanceReview = reviewJsObject?.reviewTemplateType === ReviewTemplateTypeType.performanceReview;
  const isFeedback360 = reviewJsObject?.reviewTemplateType === ReviewTemplateTypeType.review360;
  const isSelf360 = reviewJsObject?.self360;

  const isCurrentUserCreator = reviewJsObject?.creatorSlug === currentUserSlug;
  const isCurrentUserCompiler = reviewJsObject?.compilerSlug === currentUserSlug;
  const isCurrentUserReceiver = reviewJsObject?.receiverSlug === currentUserSlug;

  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);
  const reviewTemplateVersion = reviewJsObject?.reviewTemplateVersion;

  const isCancelled = !!reviewJsObject?.cancelledAt;
  const isShared = !!reviewJsObject?.unlockedAt;
  const isClosed = !!reviewJsObject?.closedAt;
  const isCompleted = !!reviewJsObject?.completedAt;

  const compilationForm = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, {
      reviewSlug: slug,
      formType: FormTypeType.compilation,
    }).first()
  );

  const selfPeerReviewFormV1 = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndUserSlugAndFormType(state, {
      reviewSlug: slug,
      userSlug: receiverSlug,
      formType: FormTypeType.peer_review,
    })
  );

  const selfPeerReviewFormV2 = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndUserSlugAndFormType(state, {
      reviewSlug: slug,
      userSlug: receiverSlug,
      formType: FormTypeType.self_peer_review,
    })
  );

  const peerReviewForms = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, { reviewSlug: slug, formType: FormTypeType.peer_review })
  );

  const managerReviewForm = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, {
      reviewSlug: slug,
      formType: FormTypeType.manager_peer_review,
    }).first()
  );

  const mergedSelfAndPeerReviews =
    reviewTemplateVersion === ReviewTemplateVersion.two && selfPeerReviewFormV2
      ? OrderedMap([[selfPeerReviewFormV2?.get('slug'), selfPeerReviewFormV2]]).merge(peerReviewForms)
      : peerReviewForms;

  const mergedReviewForms = managerReviewForm
    ? mergedSelfAndPeerReviews.merge(OrderedMap([[managerReviewForm?.get('slug'), managerReviewForm]]))
    : mergedSelfAndPeerReviews;

  const totalReviewFormsCount = mergedReviewForms.size;
  const submittedReviewsCount = mergedReviewForms.filter(reviewForm =>
    Boolean(ReviewFormHelpers.getSubmittedAt(reviewForm))
  ).size;

  return {
    // Review item as an immutable.js map.
    // this is still heavily used through the 360 review code,
    /// but want to quit using immutable.js as the component level
    review,
    // Review item converted from an immutable map to a plain javascript object
    reviewJsObject,
    isPerformanceReview,
    isFeedback360,
    isSelf360,
    state: reviewJsObject?.state,
    compilationForm,
    selfPeerReviewForm:
      reviewTemplateVersion === ReviewTemplateVersion.one ? selfPeerReviewFormV1 : selfPeerReviewFormV2,
    peerReviewForms,
    mergedSelfAndPeerReviews,
    mergedReviewForms,
    managerReviewForm,
    reviewTemplateVersion,
    isCurrentUserCreator,
    isCurrentUserCompiler,
    isCurrentUserReceiver,
    isCompleted,
    isCancelled,
    isShared,
    isClosed,
    totalReviewFormsCount,
    submittedReviewsCount,
    receiver,
    compiler,
    creator,
  };
};
